import {
  ChangeEvent,
  FocusEvent,
  KeyboardEventHandler,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { InlineMessage, InputTitle } from "chameleon/lib/components";
import {
  inputContentClass,
  inputCoreClass,
  inputDisabledClass,
  inputElementStyles,
  inputErrorClass,
  inputIframeClass,
  inputTypographyClass,
  inputWrapperClass,
} from "chameleon/lib/components/input/input.css";
import {
  getHandleOnChange,
  useMaskValueChangeHandler,
} from "chameleon/lib/components/text-input/handle-on-change";

export interface TextInputProps {
  /**
   * Aria Label for accessibility
   */
  ariaLabel: string;
  /**
   * caption for the input
   */
  captionMessage?: string;
  /**
   * Error message for the input
   */
  errorMessage?: string;
  /**
   * Error Message Key for caption test id
   */
  errorMessageKey?: string;
  /**
   * What do you want to display on the left?
   */
  ContentLeft?: JSX.Element;
  /**
   * What do you want on the right?
   */
  ContentRight?: JSX.Element;
  /**
   * ID used for accessibility
   */
  id: string;
  /**
   * Info used for the input title's info icon
   */
  info?: string;
  /**
   * Is the input disabled
   */
  isDisabled?: boolean;
  /**
   * Is the input required?
   */
  isRequired?: boolean;
  /**
   * onBlur handler
   */
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  /**
   * onChange handler
   */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /**
   * onFocus handler
   */
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  /**
   * Placeholder for the input
   */
  placeholder?: string;
  /**
   * Title for the input
   */
  title?: string;
  /**
   * Max length for the input
   */
  maxLength?: number;
  /**
   * pattern for allowed characters in input
   */
  pattern?: string;
  /**
   * value of input
   */
  value?: string;
  /**
   * Tab index for the input
   */
  tabIndex?: number;
  /**
   * Autocomplete for the input
   */
  autocomplete?: string;
  /**
   * Flag for hiding messaging in iframe wrapper
   */
  inIframe?: boolean;
  /**
   * Outside tooltip component
   */
  OutsideRightElement?: JSX.Element;
  /**
   * inputMode = "numeric" for mobile
   */
  inputMode?: "numeric" | "text";
  /**
   * onKeyUp = handle key up events
   */
  onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
  /**
   * onKeyDown = handle key down events
   */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  /**
   * shouldMaskValue = true to mask the value
   */
  shouldMaskValue?: boolean;
}

/**
 * Primary TextInput component. Accepts a forwarded Ref!
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      ariaLabel,
      ContentLeft,
      ContentRight,
      info,
      onFocus,
      onBlur,
      isDisabled = false,
      isRequired = false,
      maxLength = undefined,
      errorMessage,
      errorMessageKey,
      captionMessage,
      id,
      title,
      pattern,
      onChange,
      placeholder,
      value,
      tabIndex,
      autocomplete,
      inIframe = false,
      OutsideRightElement,
      inputMode = "text",
      onKeyDown,
      onKeyUp,
      shouldMaskValue = false,
    }: TextInputProps,
    ref
  ) => {
    const [maskedValue, setMaskedValue] = useState<string>(value ?? "");
    const inputRef = useRef<HTMLInputElement>(null);
    const cursorPositionRef = useRef<number | null>(null);

    useMaskValueChangeHandler({
      value,
      shouldMaskValue,
      setMaskedValue,
      cursorPositionRef,
    });

    // Forward the input reference to parent components
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const handleChange = getHandleOnChange({
      onChange,
      value,
      shouldMaskValue,
      maskedValue,
    });

    const handleChangeWithCursorPosition = (
      e: ChangeEvent<HTMLInputElement>
    ) => {
      if (inputRef.current) {
        cursorPositionRef.current = e.target.selectionStart;
      }

      if (shouldMaskValue) {
        handleChange(e);
      } else {
        onChange(e);
      }
    };

    const hasMessage = captionMessage || errorMessage;
    const captionID = `${id}-${errorMessageKey}-caption`;
    const errorStyles = errorMessage ? inputErrorClass : "";
    const disabledStyles = isDisabled ? inputDisabledClass : "";

    return (
      <div className={`${inputWrapperClass}`}>
        {title && <InputTitle info={info} htmlFor={id} title={title} />}
        <div className={`${inputContentClass}`}>
          <div
            className={`${inputCoreClass} ${errorStyles} ${disabledStyles} ${
              inIframe ? inputIframeClass : ""
            } validateField`}
          >
            {ContentLeft}
            <input
              aria-required={isRequired}
              aria-label={ariaLabel}
              aria-describedby={errorMessage ? captionID : undefined}
              disabled={isDisabled}
              type="text"
              placeholder={placeholder}
              id={id}
              name={id}
              maxLength={maxLength}
              pattern={pattern}
              onBlur={onBlur}
              value={shouldMaskValue ? maskedValue : value}
              onChange={handleChangeWithCursorPosition}
              onFocus={onFocus}
              ref={inputRef}
              tabIndex={tabIndex}
              className={`${inputTypographyClass} ${inputElementStyles}`}
              autoComplete={autocomplete}
              data-testid={id}
              inputMode={inputMode}
              onKeyDown={onKeyDown}
              onKeyUp={onKeyUp}
            />
            {ContentRight}
          </div>

          {!inIframe && OutsideRightElement}
        </div>
        {hasMessage && !inIframe && (
          <InlineMessage
            id={captionID}
            message={errorMessage || captionMessage}
            type={errorMessage ? "error" : "info"}
          />
        )}
      </div>
    );
  }
);

TextInput.displayName = "TextInput";
